// Library Methods
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import SelectLensButton from "../card-ui/SelectLensButton/SelectLensButton";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import ExamCardContainer from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_PROGRESSIVE_SIMULATION_CARD,
  DEFAULT_SIM_ENVIRONMENT,
  ENABLED_SIM_ENVIRONMENTS,
  examCardEnums,
} from "src/constants/enums";

// Hooks
import { useProgressiveLenses } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useProgressiveLenses";
import { useSimulationEnvironmentTypes } from "src/services/clinics/hooks/clinic-lenses/simulation-environments/useSimulationEnvironmentTypes";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { getProgressiveTooltipText } from "src/utils/exams/lensSimulationHelper";

type ProgressiveCardType = {
  selectedSimCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const ProgressiveCard = ({
  selectedSimCard,
  updateExamCardProperty,
  onRemoveCard,
}: ProgressiveCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const defaultSimEnv = useMemo(
    () => ({
      displayName: t(DEFAULT_SIM_ENVIRONMENT),
      value: DEFAULT_SIM_ENVIRONMENT,
    }),
    [t]
  );
  const [envType, setEnvType] = useState({ displayName: "", value: "" });

  const examCardInfo = selectedSimCard ?? DEFAULT_PROGRESSIVE_SIMULATION_CARD;
  const { lensIds, binocular, simulationEnvironmentType } = examCardInfo;

  const {
    data: lensesData,
    isLoading: isLoadingLenses,
    isError: isErrorLenses,
  } = useProgressiveLenses(clinicInfo?.id);

  const {
    data: envData,
    isLoading: isLoadingEnvs,
    isError: isErrorEnvs,
  } = useSimulationEnvironmentTypes();

  const formattedEnvOptions = useMemo(
    () =>
      (envData ?? [])
        ?.filter((data) => ENABLED_SIM_ENVIRONMENTS.includes(data))
        ?.map((data) => ({
          displayName: t(data),
          value: data,
        })),
    [t, envData]
  );

  const hasSimEnvs = useMemo(
    () =>
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !isLoadingEnvs &&
      !isErrorEnvs,
    [formattedEnvOptions, isLoadingEnvs, isErrorEnvs]
  );

  const hasLenses = useMemo(
    () =>
      lensesData &&
      lensesData?.length > 0 &&
      !isLoadingLenses &&
      !isErrorLenses,
    [lensesData, isLoadingLenses, isErrorLenses]
  );

  // on adding a new lens simulation, auto-select the first lens
  useEffect(() => {
    if (lensesData && lensesData?.length > 0 && lensIds && !lensIds?.length) {
      updateExamCardProperty("lensIds", [lensesData?.[0]?.id]);
    }
  }, [lensIds, lensesData, updateExamCardProperty]);

  // initial setup of simulation environment
  useEffect(() => {
    if (
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !envType.value
    ) {
      const valueToUse = simulationEnvironmentType
        ? {
            displayName: t(simulationEnvironmentType),
            value: simulationEnvironmentType,
          }
        : defaultSimEnv;

      setEnvType({ ...valueToUse });
      updateExamCardProperty("simulationEnvironmentType", valueToUse?.value);
    }
  }, [
    t,
    envType.value,
    defaultSimEnv,
    simulationEnvironmentType,
    formattedEnvOptions,
    updateExamCardProperty,
  ]);

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  return (
    <ExamCardContainer>
      <ExamCardHeader
        label={t(examCardEnums.PROGRESSIVE_SIMULATION.buttonLabel)}
        onRemoveCard={onRemoveCard}
      />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        {hasSimEnvs && (
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Starting Simulation Environment")}
            </Typography>
            <SelectDropdown
              id="sim-env-type-dropdown"
              dropdownOptions={formattedEnvOptions}
              value={envType.displayName}
              handleChange={(event) => {
                const value = formattedEnvOptions?.find(
                  (option) => option.displayName === event.target.value
                );

                if (value) {
                  setEnvType({
                    ...value,
                  });
                  updateExamCardProperty(
                    "simulationEnvironmentType",
                    value.value
                  );
                }
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <TitleWithTooltip
            title={t("Progressive Types")}
            tooltip={
              <div>
                {t(
                  "Controls which progressive lenses appear in the simulation."
                )}
              </div>
            }
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid
            sx={{
              display: "grid",
              gridTemplateColumns:
                !hasLenses && !isLoadingLenses
                  ? "repeat(1, 1fr)"
                  : "repeat(2, 1fr)",
              gap: "8px",
            }}
          >
            {isLoadingLenses && (
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {t("Loading...")}
              </Typography>
            )}
            {hasLenses &&
              lensesData?.map((lensType) => (
                <SelectLensButton
                  key={`lens-button-${lensType.id}`}
                  lensType={lensType}
                  selectedLensArray={lensIds}
                  updateExamCardProperty={updateExamCardProperty}
                  examAlgorithmPath={
                    examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath
                  }
                  tooltip={getProgressiveTooltipText(lensType, t)}
                />
              ))}
            {!hasLenses && !isLoadingLenses && (
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {t("No lenses found. You can add and remove lenses from the")}{" "}
                <Link
                  to="/settings"
                  style={{ color: "#FFF", textDecoration: "underline" }}
                >
                  {t("clinic settings page")}
                </Link>
                .
              </Typography>
            )}
          </Grid>
        </Box>

        {hasLenses && (
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            marginTop="auto"
          >
            <TitleWithTooltip
              title={t("Eye")}
              tooltip={t(
                "Choose the binocular option to test both eyes at once or the monocular option to test one eye at a time."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />
            <Grid container item display="flex" gap="8px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={Boolean(binocular)}
                  updateValue={() => updateExamCardProperty("binocular", true)}
                  label={t("Binocular")}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </ExamCardContainer>
  );
};

export default ProgressiveCard;
